<template>
    <div v-if="activeOpportunities.length" class="OrgOpportunitiesGroup mb-8">
        <CHeading tag="h2" class="mb-6">{{ title }}</CHeading>
        <CMasonryGrid :columns="3">
            <CMasonryCard
                v-for="item in activeOpportunities"
                :key="item.id"
                :image-link="getImageLink(item)"
                :toasts="getToasts(item)"
                :toast-type="getToastTypes(item)"
                :title="getTitle(item)"
                :subscript="getSubscript(item)"
                :attributes="getAttributes(item)"
                :attribute-icon-names="getAttributeIcons(item)"
                :tags="getTags(item)"
                :page="getPageName(item)"
                :page-params="getPageParams(item)"
                @tag-click="handleTagClick"
            />
        </CMasonryGrid>
    </div>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue'
import { VisibilityType } from '#shared/types/visibility'
import { isPublicVolunteeringWeekDraftProject } from '#shared/utils/publicProjectFilters'
import type { Job } from '#shared/types/Job'
import { JobPlaceType } from '#shared/types/Job'
import type { Skill } from '#shared/types/Skill'
import { SkillType } from '#shared/types/Skill'
import type { Project } from '#shared/types/Project'
import { skillTypeDict } from '#shared/types/dictionary'
import CHeading from '@/components/ui/CHeading.vue'
import CMasonryGrid from '@/components/ui/CMasonryGrid.vue'
import CMasonryCard from '@/components/ui/CMasonryCard.vue'

// Union type for all opportunity types with better compatibility  
type OpportunityItem = (Job | Skill | Project) & { [key: string]: any }

interface Props {
    title: string
    items: OpportunityItem[]
    // Collection the items come from. Server-rendered org pages send trimmed card data,
    // so the property sniffing below cannot be relied on there.
    itemType?: 'job' | 'skill' | 'project'
}

const props = defineProps<Props>()

// Utility function to filter public opportunities
const filterPublicOpportunities = (
    opportunities: OpportunityItem[]
): OpportunityItem[] => {
    return opportunities.filter(
        (item: OpportunityItem) =>
            item.visibility === VisibilityType.published || isPublicVolunteeringWeekDraftProject(item)
    )
}

const activeOpportunities = computed<OpportunityItem[]>(() => {
    return filterPublicOpportunities(props.items)
})

// Type guard functions
const isJob = (item: OpportunityItem): item is Job => {
    if (props.itemType) return props.itemType === 'job'
    return 'jobPlace' in item || 'formOfCollaboration' in item || 'whoTypes' in item
}

const isSkill = (item: OpportunityItem): item is Skill => {
    if (props.itemType) return props.itemType === 'skill'
    return 'type' in item && Object.values(SkillType).includes(item.type as SkillType)
}

const isProject = (item: OpportunityItem): item is Project => {
    if (props.itemType) return props.itemType === 'project'
    return 'how' in item || 'duration' in item || 'maturity' in item || 'wanted' in item || 'expertise' in item || 
           'vision' in item || 'benefits' in item || 'impact' in item || 'maxVolunteerCount' in item ||
           'receivedVolunteerRequestsCount' in item || 'area' in item || 'geoRegion' in item || 'geoCity' in item
}

// Helper function to determine the item type based on its properties
const getItemType = (item: OpportunityItem): 'job' | 'skill' | 'project' => {
    if (props.itemType) return props.itemType

    // Check for job-specific properties FIRST (jobs also have benefits/expertise which overlap with projects)
    if (item.jobPlace || item.formOfCollaboration || item.whoTypes) {
        return 'job'
    }

    // Check for skill-specific properties
    if (item.type && Object.values(SkillType).includes(item.type as SkillType)) {
        return 'skill'
    }

    // Check for project-specific properties (only after ruling out jobs)
    if (item.how || item.duration || item.maturity || item.wanted ||
        item.vision || item.impact || item.maxVolunteerCount !== undefined ||
        item.receivedVolunteerRequestsCount !== undefined || item.area || item.geoRegion || item.geoCity) {
        return 'project'
    }

    // Additional project patterns
    if (item.memberIds && Array.isArray(item.memberIds) && item.orgMeta) {
        return 'project'
    }

    // Skills often have coverImg without orgMeta or have specific event properties
    if (item.coverImg && (!item.orgMeta || item.isOnline !== undefined || item.city)) {
        return 'skill'
    }

    // Jobs typically have endDate for application deadlines
    if (item.endDate && !item.how && !item.wanted) {
        return 'job'
    }

    // Default to project if we have orgMeta (most org opportunities are projects)
    if (item.orgMeta) {
        return 'project'
    }

    return 'job' // Final fallback
}

// CMasonryCard helper functions
const getImageLink = (item: OpportunityItem): string => {
    const itemType = getItemType(item)

    switch (itemType) {
        case 'job':
            return item.image || item.orgImage || item.orgMeta?.image || ''
        case 'skill':
            return item.coverImg || item.orgImage || item.orgMeta?.image || ''
        case 'project':
            return item.coverImg || item.image || item.orgImage || item.orgMeta?.image || ''
        default:
            return ''
    }
}

const toDate = (value: unknown): Date | null => {
    if (!value) return null
    if (typeof value === 'object' && 'toDate' in value && typeof value.toDate === 'function') {
        return value.toDate()
    }

    const date = new Date(value as string | number | Date)
    return Number.isNaN(date.getTime()) ? null : date
}

const hasSupportedDisabilities = (disabilities: unknown): boolean => {
    if (Array.isArray(disabilities)) return disabilities.length > 0
    return Boolean(
        disabilities &&
            typeof disabilities === 'object' &&
            'supported' in disabilities &&
            (disabilities as { supported?: boolean }).supported
    )
}

const getToasts = (item: OpportunityItem): string[] => {
    const toasts: string[] = []
    
    if (isJob(item)) {
        if (item.endDate) {
            const date = toDate(item.endDate)
            if (date) toasts.push(date.toLocaleDateString('sk-SK'))
        }
        if ((Array.isArray(item.jobPlace) && item.jobPlace.includes(JobPlaceType.hybrid)) || item.remote) {
            toasts.push('ONLINE')
        }
        if (hasSupportedDisabilities(item.disabilities)) {
            toasts.push('Inkluzívne')
        }
    }
    
    if (isSkill(item)) {
        if (item.type === SkillType.workgroup) {
            toasts.push('Prihlás sa')
        }
        if (item.isOnline || item.remote) {
            toasts.push('ONLINE')
        }
        if (hasSupportedDisabilities(item.disabilities)) {
            toasts.push('Inkluzívne')
        }
    }
    
    if (isProject(item)) {
        // Main project badge
        toasts.push('Dobrovoľníctvo')
        
        // Online badge
        if (item.remote) {
            toasts.push('ONLINE')
        }
        
        // Inclusive badge
        if (hasSupportedDisabilities(item.disabilities)) {
            toasts.push('Inkluzívne')
        }
        
        // End date if available
        if (item.endDate) {
            const date = toDate(item.endDate)
            if (date) toasts.push(date.toLocaleDateString('sk-SK'))
        }
    }
    
    return toasts
}

const getToastTypes = (item: OpportunityItem): string[] => {
    const types: string[] = []
    
    if (isJob(item)) {
        if (item.endDate) {
            types.push('default')
        }
        if ((Array.isArray(item.jobPlace) && item.jobPlace.includes(JobPlaceType.hybrid)) || item.remote) {
            types.push('success')
        }
        if (hasSupportedDisabilities(item.disabilities)) {
            types.push('info')
        }
    }
    
    if (isSkill(item)) {
        if (item.type === SkillType.workgroup) {
            types.push('red')  // Using 'red' instead of 'warning'
        }
        if (item.isOnline || item.remote) {
            types.push('success')
        }
        if (hasSupportedDisabilities(item.disabilities)) {
            types.push('info')
        }
    }
    
    if (isProject(item)) {
        // Main project badge - use primary color for volunteering
        types.push('primary')
        
        // Online badge
        if (item.remote) {
            types.push('success')
        }
        
        // Inclusive badge
        if (hasSupportedDisabilities(item.disabilities)) {
            types.push('info')
        }
        
        // End date if available
        if (item.endDate) {
            types.push('default')
        }
    }
    
    return types
}

const getTitle = (item: OpportunityItem): string => {
    return item.title || ''
}

const getSubscript = (item: OpportunityItem): string => {
    if (isJob(item)) {
        return item.orgName || item.orgMeta?.title || ''
    }

    if (isSkill(item)) {
        if (!item.type) return ''
        return skillTypeDict[item.type as SkillType] || ''
    }

    if (isProject(item)) {
        return item.orgName || item.orgMeta?.title || ''
    }

    return ''
}

const getAttributes = (item: OpportunityItem): string[] => {
    const itemType = getItemType(item)
    const attributes: string[] = []
    
    switch (itemType) {
        case 'job':
            if (item.formOfCollaboration?.length) {
                attributes.push(item.formOfCollaboration.join(', '))
            }
            if (item.location) {
                attributes.push(item.location)
            }
            if (item.endDate) {
                const date = toDate(item.endDate)
                if (date) attributes.push(date.toLocaleDateString('sk-SK'))
            }
            break
        case 'skill':
            const isEvent = item.type === SkillType.kurz || item.type === SkillType.event
            
            if (isEvent && item.startDate) {
                const date = toDate(item.startDate)
                if (date) attributes.push(date.toLocaleDateString('sk-SK'))
            }
            if (isEvent && !item.isOnline && item.city) {
                attributes.push(item.city)
            }
            if (isEvent && item.isOnline) {
                attributes.push('Online')
            }
            break
        case 'project':
            if (item.location) {
                attributes.push(item.location)
            }
            if (item.startDate) {
                const startDate = toDate(item.startDate)
                if (startDate) attributes.push(startDate.toLocaleDateString('sk-SK'))
            }
            if (item.endDate) {
                const endDate = toDate(item.endDate)
                if (endDate) attributes.push(endDate.toLocaleDateString('sk-SK'))
            }
            break
    }
    
    return attributes.filter(Boolean)
}

const getAttributeIcons = (item: OpportunityItem): string[] => {
    const itemType = getItemType(item)
    const icons: string[] = []
    
    switch (itemType) {
        case 'job':
            if (item.formOfCollaboration) {
                icons.push('work-type')
            }
            if (item.location) {
                icons.push('location-outline')
            }
            if (item.endDate) {
                icons.push('calendar')
            }
            break
        case 'skill':
            const isEvent = item.type === SkillType.kurz || item.type === SkillType.event
            
            if (isEvent && item.startDate) {
                icons.push('calendar')
            }
            if (isEvent && !item.isOnline && item.city) {
                icons.push('location-outline')
            }
            if (isEvent && item.isOnline) {
                icons.push('globe')
            }
            break
        case 'project':
            if (item.location) {
                icons.push('location-outline')
            }
            if (item.startDate) {
                icons.push('calendar')
            }
            if (item.endDate) {
                icons.push('calendar')
            }
            break
    }
    
    return icons.filter(Boolean)
}

const getTags = (item: OpportunityItem): string[] => {
    return item.topics || []
}

const getPageName = (item: OpportunityItem): string => {
    const itemType = getItemType(item)
    
    switch (itemType) {
        case 'job':
            return 'praca-slug'
        case 'skill':
            return 'vzdelavanie-slug'
        case 'project':
            return 'dobrovolnictvo-dobrovolnicke-projekty-id'
        default:
            return ''
    }
}

const getPageParams = (item: OpportunityItem): Record<string, any> => {
    const itemType = getItemType(item)
    
    switch (itemType) {
        case 'job':
            return { slug: item.slug || item.id }
        case 'skill':
            return { slug: item.slug || item.id }
        case 'project':
            return { id: item.slug || item.id }
        default:
            return { slug: item.slug || item.id }
    }
}

const handleTagClick = (tag: string): void => {
    // Tag click handling - could emit event or navigate
    console.log('Tag clicked:', tag)
}

// Debug helper - log item types for verification
const logItemTypes = () => {
    if (import.meta.client && activeOpportunities.value.length > 0) {
        console.log(`=== OrgOpportunitiesGroup Debug (${props.title}) ===`)
        activeOpportunities.value.forEach((item, index) => {
            const type = getItemType(item)
            const image = getImageLink(item) 
            const page = getPageName(item)
            console.log(`Item ${index + 1}: "${item.title}" -> Type: ${type}, Image: ${image}, Route: ${page}`)
        })
    }
}

// Call debug in development
if (import.meta.client && import.meta.dev) {
    watch(activeOpportunities, logItemTypes, { immediate: true })
}
</script>
